import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { AppLayout } from '../../layout/AppLayout';
import { DashboardLayout } from '../../layout/DashboardLayout';
import { PreStep } from './PreStep/PreStep';
import { QuestionnaireFooter } from './QuestionnaireFooter';
import { QuestionnaireHeader } from './QuestionnaireHeader';
import { Stepper } from './Manual/Stepper/Stepper';
import { PATHS } from '../../constants/Paths.constants';
import { useEffect, useRef, useState } from 'react';
import AIPoweredQuestionnaire from './AIPowered/AIPoweredQuestionnaire';
import { AssessmentType } from '../../types/AISystems.types';
import Loader from '../ui/Loader/Loader';
import { UnableToLoadContent } from '../ui/UnableToLoadContent/UnableToLoadContent';
import { isServerErrorStatus } from '../../utils/apiError';
import { clearQuestionnaireServerError } from '../../store/slices/stepperSlice';
import { clearAIPoweredError } from '../../store/slices/aiPoweredSlice';
import { setAISystemStatus } from '../../store/slices/aiSystemsSlice';

type QuestionnaireContentProps = {
  assessmentType: AssessmentType | null;
  showPreStep: boolean;
  hasQuestionnaireServerError: boolean;
  handleRetry: () => void;
};

const QuestionnaireContent = ({
  assessmentType,
  showPreStep,
  hasQuestionnaireServerError,
  handleRetry
}: QuestionnaireContentProps) => {
  if (hasQuestionnaireServerError) {
    return <UnableToLoadContent onRetry={handleRetry} />;
  }

  if (showPreStep) return <PreStep />;

  switch (assessmentType) {
    case 'QUESTIONNAIRE':
      return <Stepper />;
    case 'AI_POWERED':
      return <AIPoweredQuestionnaire />;
    default:
      return null;
  }
};

export const Questionnaire = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const questionnaireContentRef = useRef<HTMLDivElement>(null);

  const {
    showPreStep,
    enableStepper,
    startAiSystemBodyRequest,
    isChangingAssessment,
    isFinalizingAIReport,
    serverErrorStatus
  } = useAppSelector((state) => state.stepper);

  const [hasCheckedAccess, setHasCheckedAccess] = useState(false);

  const assessmentType = startAiSystemBodyRequest.assessmentType;
  const followUpQuestion = useAppSelector(
    (state) => state.aiPowered.currentJob?.followUpQuestion
  );
  const aiPoweredErrorStatus = useAppSelector(
    (state) => state.aiPowered.errorStatus
  );
  const hasQuestionnaireServerError =
    isServerErrorStatus(serverErrorStatus) ||
    isServerErrorStatus(aiPoweredErrorStatus);

  const handleRetry = () => {
    dispatch(clearQuestionnaireServerError());
    dispatch(clearAIPoweredError());
    dispatch(setAISystemStatus(null));
    navigate(PATHS.AI_SYSTEMS);
  };

  useEffect(() => {
    if (!enableStepper) {
      navigate(PATHS.AI_SYSTEMS);
    } else {
      setHasCheckedAccess(true);
    }
  }, [enableStepper, navigate]);

  useEffect(() => {
    if (
      assessmentType !== 'AI_POWERED' ||
      isChangingAssessment ||
      isFinalizingAIReport ||
      !followUpQuestion
    ) {
      return;
    }

    const animationFrameId = requestAnimationFrame(() => {
      questionnaireContentRef.current?.scrollTo({
        top: questionnaireContentRef.current.scrollHeight,
        behavior: 'smooth'
      });
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [
    assessmentType,
    followUpQuestion,
    isChangingAssessment,
    isFinalizingAIReport
  ]);

  // block UI render, prevents flashing content before redirect
  if (!hasCheckedAccess) return null;

  return (
    <AppLayout>
      <DashboardLayout title="AI Systems">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex-1 max-w-none w-full flex flex-col p-4 h-[calc(100vh_-_160px)]">
          <QuestionnaireHeader />
          <div
            ref={questionnaireContentRef}
            className="overflow-y-auto h-full flex flex-col gap-5"
          >
            {isChangingAssessment || isFinalizingAIReport ? (
              <div className="flex h-full items-center justify-center">
                <Loader />
              </div>
            ) : (
              <QuestionnaireContent
                assessmentType={assessmentType}
                showPreStep={showPreStep}
                hasQuestionnaireServerError={hasQuestionnaireServerError}
                handleRetry={handleRetry}
              />
            )}
          </div>
          {!isChangingAssessment && !hasQuestionnaireServerError && (
            <QuestionnaireFooter
              questionnaireContentRef={questionnaireContentRef}
            />
          )}
        </div>
      </DashboardLayout>
    </AppLayout>
  );
};
